/**
 * Axios instance with a Security by Design baseline:
 *  - Pulls a CSRF token from the backend and attaches it to every state-
 *    changing request (`X-CSRF-Token`).
 *  - Sends an `X-Requested-With` header so the server can reject bare
 *    cross-origin form submissions.
 *  - Default 15s timeout to mitigate hung requests.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

let csrfToken = null;
let csrfPromise = null;

async function fetchCsrfToken() {
  const { data } = await axios.get(`${API_BASE}/csrf-token`, { timeout: 10000 });
  csrfToken = data.csrf_token;
  return csrfToken;
}

export async function getCsrfToken({ force = false } = {}) {
  if (csrfToken && !force) return csrfToken;
  if (!csrfPromise) {
    csrfPromise = fetchCsrfToken().finally(() => {
      csrfPromise = null;
    });
  }
  return csrfPromise;
}

export const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: {
    "X-Requested-With": "XMLHttpRequest",
  },
});

apiClient.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const token = await getCsrfToken();
    config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    // If CSRF was rejected, refresh once and let caller retry.
    if (error?.response?.status === 403 && error?.response?.data?.error === "invalid_csrf_token") {
      await getCsrfToken({ force: true });
    }
    return Promise.reject(error);
  },
);
